import { UserRoles } from '@chiroup/core/types/User.type';
import React, { use, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Link } from 'react-router-dom';
import { MeContext } from '../../contexts/me.context';
import Modal from '../common/Modal';

const SwitchLocations = () => {
  const { activeLocations, selectLocation, hasRole, me } = use(MeContext);

  const [switchLocationsModalOpen, setSwitchLocationsModalOpen] = useState<boolean>(false);
  const [focusedLocationIndex, setFocusedLocationIndex] = useState<number>(0);
  const locationRefs = useRef<(HTMLLIElement | null)[]>([]);

  // Listen for global location switcher custom event
  useEffect(() => {
    const handleOpenLocationSwitcher = () => {
      if (activeLocations?.length > 1) {
        setSwitchLocationsModalOpen(true);
      }
    };

    document.addEventListener('openLocationSwitcher', handleOpenLocationSwitcher);

    return () => {
      document.removeEventListener('openLocationSwitcher', handleOpenLocationSwitcher);
    };
  }, [activeLocations]);

  // Direct keyboard shortcut for location switcher
  useHotkeys(
    'ctrl+shift+l',
    () => {
      if (activeLocations?.length > 1) {
        setSwitchLocationsModalOpen(true);
      }
    },
    {
      preventDefault: true,
      enableOnFormTags: false,
    }
  );

  // Reset focused location when modal opens and set to currently selected location
  useEffect(() => {
    if (switchLocationsModalOpen && activeLocations?.length > 0) {
      // Find the index of the currently selected location
      const currentLocationIndex = activeLocations.findIndex(
        loc => loc.ID === me?.selectedLocation
      );

      // If found, set it as the focused index, otherwise default to 0
      const indexToFocus = currentLocationIndex >= 0 ? currentLocationIndex : 0;
      setFocusedLocationIndex(indexToFocus);

      // Focus the selected location after a short delay to ensure the modal is rendered
      setTimeout(() => {
        if (locationRefs.current[indexToFocus]) {
          locationRefs.current[indexToFocus].focus();
        }
      }, 100);
    }
  }, [switchLocationsModalOpen, activeLocations, me?.selectedLocation]);

  // Handle keyboard navigation within the modal
  const handleKeyNavigation = (event: React.KeyboardEvent<HTMLLIElement>, index: number) => {
    let nextIndex: number;
    let prevIndex: number;

    switch (event.key) {
      case 'ArrowDown':
      case 'Tab':
        event.preventDefault();
        nextIndex = (index + 1) % (activeLocations?.length || 1);
        setFocusedLocationIndex(nextIndex);
        locationRefs.current[nextIndex]?.focus();
        break;
      case 'ArrowUp':
      case 'ShiftTab':
        event.preventDefault();
        prevIndex = (index - 1 + (activeLocations?.length || 1)) % (activeLocations?.length || 1);
        setFocusedLocationIndex(prevIndex);
        locationRefs.current[prevIndex]?.focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();

        if (activeLocations && activeLocations[index]) {
          selectLocation(activeLocations[index].ID);
          setSwitchLocationsModalOpen(false);
        }

        break;
      case 'Escape':
        event.preventDefault();

        setSwitchLocationsModalOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <>
      {activeLocations?.length > 1 && (
        <div
          onClick={() => {
            setSwitchLocationsModalOpen(true);
          }}
          className="text-primary-600 hover:text-primary-400 dark:text-dark-gray-400 dark:hover:text-dark-gray-50  cursor-pointer leading-4 text-xs"
        >
          Change location
        </div>
      )}
      <Modal
        isOpen={switchLocationsModalOpen}
        close={() => {
          setSwitchLocationsModalOpen(false);
        }}
      >
        <div className="flex items-center justify-center flex-col">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <h2 className="mt-6 text-center text-3xl leading-9 font-extrabold text-gray-900 dark:text-dark-gray-50">
              Select a location
            </h2>
            {hasRole([UserRoles.Admin]) && (
              <p className="text-center text-sm leading-5 text-gray-600 max-w">
                <Link
                  to="/settings/clinic/locations"
                  className="font-medium text-primary-600 hover:text-primary-400 focus:outline-none focus:underline transition ease-in-out duration-150"
                >
                  Add a new location
                </Link>
              </p>
            )}
          </div>

          <div className="bg-white dark:bg-dark-gray-800 shadow overflow-hidden sm:rounded-md mt-8 w-full">
            <ul>
              {activeLocations?.map((loc, i) => (
                <li
                  key={i}
                  ref={el => {
                    if (el) {
                      locationRefs.current[i] = el;
                    }
                  }}
                  tabIndex={0}
                  className={[
                    'hover:bg-gray-50 dark:hover:bg-dark-gray-700 focus:outline-none focus:bg-gray-50 dark:focus:bg-dark-gray-700 transition duration-150 ease-in-out cursor-pointer',
                    i ? 'border-t border-gray-300 dark:border-dark-gray-600' : '',
                    focusedLocationIndex === i
                      ? 'bg-primary-50 dark:bg-dark-gray-600 ring-2 ring-primary-500'
                      : '',
                  ].join(' ')}
                  onClick={() => {
                    selectLocation(loc.ID);
                    setSwitchLocationsModalOpen(false);
                  }}
                  onKeyDown={e => handleKeyNavigation(e, i)}
                >
                  <div className="flex items-center px-4 py-4 sm:px-6">
                    <div className="min-w-0 flex-1 flex items-center w-full">
                      <div className="shrink-0">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`h-12 w-12 ${focusedLocationIndex === i ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-dark-gray-400'}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1 px-4 w-full">
                        <div>
                          {loc.name && (
                            <div className="text-sm leading-5 font-medium text-primary-600 truncate">
                              {loc.name}
                            </div>
                          )}
                          <div className="text-sm leading-5 font-medium text-primary-600 truncate">
                            {loc.address1} {loc.address2}
                          </div>
                          <div className="text-sm leading-5 font-medium text-primary-600 truncate">
                            {loc.city}, {loc.state}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <svg
                        className={`h-5 w-5 ${focusedLocationIndex === i ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-dark-gray-200'}`}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SwitchLocations;
